/**
 * Knowledge documents RPC client (co-located eq instance, WP3.7.3b-22d): the
 * firm's knowledge-base library on `KnowledgeDocumentsRpc`, which replaced
 * the `/api/documents-v2` REST routes. An entity, because two widgets read it:
 * the knowledge-base hub (the listing, folders, rename, delete, download) and
 * the upload tracker (prepare, retry, complete).
 *
 * `knowledgeDocumentsEq` serves the listing query; `callKnowledgeDocuments`
 * runs one call from a handler and resolves with its answer, rejecting on any
 * failure (as the old `fetch` + `res.ok` checks did) or when `signal` aborts.
 */
import { Effect, Layer } from "effect"
import { RpcClient, RpcSerialization } from "@effect/rpc"
import type { QueryClient } from "@tanstack/react-query"
import { AppHttpClient, withQueryClientScope } from "@/shared/scope"
import { createEffectQueryNoAbort } from "@/lib/api/effect-query-utils"
import { KnowledgeDocumentsRpc } from "@/presentation/rpc/knowledge-documents-rpc"

const KnowledgeDocumentsRpcLive = RpcClient.layerProtocolHttp({
  url: "/api/rpc/knowledge-documents",
}).pipe(
  Layer.provide(AppHttpClient.layer),
  Layer.provideMerge(RpcSerialization.layerJson),
)

export const knowledgeDocumentsEq = createEffectQueryNoAbort(KnowledgeDocumentsRpcLive)

const makeKnowledgeDocumentsClient = RpcClient.make(KnowledgeDocumentsRpc)
type KnowledgeDocumentsClient = Effect.Effect.Success<typeof makeKnowledgeDocumentsClient>

/**
 * Run one knowledge documents call outside a query hook, its requests tagged
 * for the scope of the `QueryClient` the caller runs on (FIX-SCOPE-STORE-2).
 */
export const callKnowledgeDocuments = <A, E>(
  queryClient: QueryClient,
  call: (client: KnowledgeDocumentsClient) => Effect.Effect<A, E>,
  options?: { readonly signal?: AbortSignal },
): Promise<A> =>
  Effect.runPromise(
    Effect.flatMap(makeKnowledgeDocumentsClient, call).pipe(
      Effect.scoped,
      Effect.provide(KnowledgeDocumentsRpcLive),
      withQueryClientScope(queryClient),
    ),
    options?.signal === undefined ? undefined : { signal: options.signal },
  )

export { KnowledgeDocumentsRpc }
